import ReactECharts from "echarts-for-react";
import _ from "lodash";
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from "react";
import { getYAxisIndex } from "./Helpers";

const StackedVerticalBar = (props) => {
  const { barWidth,
    borderRadius,
    chartHover = "series",
    chartheight,
    data,
    events,
    isScroll,
    isScrollEndLimit,
    isScrollStartLimit,
    isScrollbarheight,
    onChartClick,
    onSprintClick,
    otherSeriesProperty,
    otherXAxisProperty,
    otherYAxisProperty,
    selectedMode,
    showLegend,
    spaceBottom,
    spaceLeft,
    spaceRight,
    spaceTop,
    targetHeight,
    toolTipConfig,
    xField,
    xNameGap,
    yAxisDataArray,
    yField,
    yNameGap,
    color,
    is3DEfffect = false,
    markPointData = [],
    YaxisLine = true
  } = props;

  const [legendSelected, setLegendSelected] = useState({});

  const formatYAxis = (value) => {
    if (value >= 1_000_000_000_000) { return `${value / 1_000_000_000_000}T`; }// Convert to T (trillions)
    if (value >= 1_000_000_000) { return `${value / 1_000_000_000}B`; } // Convert to B (billions)
    if (value >= 1_000_000) { return `${value / 1_000_000}M`; } // Convert to M (millions)
    if (value >= 1_000) { return `${value / 1_000}K`; } // Convert to K (thousands)
    return value;
  };

  const chartInstanceRef = useRef(null);
  const actualBarWidthRef = useRef(null);
  const getSeriesData = () => {
    const seriesData = [];
    if (!Array.isArray(data) || data.length === 0) {
      return seriesData;
    }
    const currentActualBarWidth = actualBarWidthRef.current;

    if (is3DEfffect === true) {
      for (let i = 0; i < data.length; i++) {
        const seriesGroup = data[i];
        const groupName = seriesGroup?.name;
        const groupData = seriesGroup?.data || [];
        const groupColors = color[groupName] || ['#cccccc', '#aaaaaa'];

        const seriesObj = {
          name: groupName,
          type: "bar",
          data: groupData,
          stack: 'total',
          barWidth: barWidth ? barWidth : [10],
          label: { show: false },
          itemStyle: {
            borderRadius: borderRadius || [0, 0, 0, 0],
            color: {
              type: "linear", x: 0, y: 0, x2: 1, y2: 0,
              colorStops: [
                { offset: 0, color: groupColors[0] },
                { offset: 0.5, color: groupColors[0] },
                { offset: 0.5, color: groupColors[1] },
                { offset: 1, color: groupColors[1] }
              ],
              global: false
            }
          },
          z: i + 1,
          ...seriesGroup.otherSeriesProperty,
          ...otherSeriesProperty,
        };

        if (Array.isArray(markPointData)) {
          const filteredMarks = markPointData?.filter(mp => mp.name === groupName);
          if (filteredMarks.length > 0) {
            seriesObj.markPoint = {
              symbol: 'diamond',
              symbolSize: currentActualBarWidth ? [currentActualBarWidth * 1, currentActualBarWidth * 0.4] : [30, 10],
              symbolOffset: [0, 0],
              label: { show: false },
              tooltip: { show: true },
              data: filteredMarks.map(mp => ({
                name: mp.name,
                coord: mp.coord,
                itemStyle: { color: mp.color },
                value: mp.value,
              }))
            };
          }
        }
        seriesData.push(seriesObj);
      }
    }
    return seriesData;
  };

  const calculateAndSetBarWidth = (chart) => {
    if (!chart || chart.isDisposed?.()) {
      return;
    }
    try {
      const chartWidth = chart.getWidth();
      if (!chartWidth) {
        return;
      }
      const option = chart.getOption();
      const categoryCount = option?.xAxis?.[0]?.data?.length || 1;
      const series = option?.series || [];

      if (!series.length) {
        return;
      }
      const barWidthValue = series[0]?.barWidth;
      const calculatedWidth = typeof barWidthValue === 'string' ? (chartWidth / categoryCount) * (parseFloat(barWidthValue) / 100) / series.length
        : (barWidthValue ?? (chartWidth / categoryCount) * 0.6);

      actualBarWidthRef.current = calculatedWidth;

      chart.setOption({
        series: getSeriesData()
      }, {
        notMerge: false,
        lazyUpdate: true
      });
    } catch {
      // Chart may have been disposed between the check and usage
    }
  };


  const getStackOption = () => {
    let options = {
      tooltip: toolTipConfig ? { ...toolTipConfig } : {
        show: true,
        trigger: 'item',
      },
      legend: {
        orient: "horizontal",
        show: showLegend ? showLegend : false,
        icon: "circle",
        type: "scroll",
        left: 10,
        selectedMode: !selectedMode ? selectedMode : "multiple",
        selected: legendSelected,
        itemWidth: 13,
        itemHeight: 13,
      },
      grid: {
        left: spaceLeft ? spaceLeft : '15%',
        right: spaceRight ? spaceRight : '10%',
        bottom: spaceBottom ? spaceBottom : '10%',
        top: spaceTop ? spaceTop : '20%',
        containLabel: true,
      },
      yAxis: {
        type: 'value',
        name: yField,
        nameLocation: "middle",
        show: true,
        minInterval: 1,
        ...getYAxisIndex(),
        axisLine: {
          show: YaxisLine ? YaxisLine : true,
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          show: true,
          formatter: function (value) {
            return formatYAxis(value);
          },
        },
        nameGap: yNameGap ? yNameGap : 30,
        ...otherYAxisProperty
      },
      xAxis: {
        type: 'category',
        name: xField,
        nameLocation: "middle",
        data: yAxisDataArray,
        barGap: '30%',
        nameGap: xNameGap ? xNameGap : 16,
        axisLine: {
          show: true,
        },
        axisTick: {
          show: false,
        },
        ...otherXAxisProperty
      },
      series: is3DEfffect ? getSeriesData() : (Array.isArray(data) ? data : []).map((seriesData) => ({
        name: seriesData.name,
        type: 'bar',
        barWidth: barWidth ? barWidth : 10,
        barCategoryGap: '40%',
        stack: 'total',
        label: {
          show: false
        },
        itemStyle: {
          borderRadius: borderRadius ? borderRadius : [0, 0, 0, 0],
          color: seriesData.color,
        },
        emphasis: {
          focus: chartHover
        },
        data: seriesData.data,
        ...seriesData.otherSeriesProperty,
        ...otherSeriesProperty
      }))
    };
    if (isScroll) {
      options.dataZoom = [
        //set the width,yAxisIndex,left,orient to horizontal
        {
          type: 'slider',
          start: 0,
          start: isScrollStartLimit ? isScrollStartLimit : 0,
          end: isScrollEndLimit ? isScrollEndLimit : 30,
          end: 30,
          height: isScrollbarheight ? isScrollbarheight : 10, // Change height to control the scrollbar's appearance
          showDetail: false,
          xAxisIndex: 0, // Change to xAxisIndex
          bottom: '0%', // Change to bottom
          handleStyle: {
            color: "#00a3ff",
          },
          dataBackground: {
            lineStyle: {
              color: "#00a3ff"
            },
            areaStyle: {
              color: 'rgba(255, 0, 0, 0.1)'
            }
          },
          orient: 'horizontal', // Change to horizontal
        },
      ];
    }
    return options;


  };

  const handleChartReady = (chart) => {
    if (!chart || chart.isDisposed?.()) {
      return;
    }
    chartInstanceRef.current = chart;
    calculateAndSetBarWidth(chart);
  };

  useEffect(() => {
    if (chartInstanceRef.current) {
      calculateAndSetBarWidth(chartInstanceRef.current);
    }
  }, [calculateAndSetBarWidth, yAxisDataArray, data, barWidth, is3DEfffect]);

  const handleChartClick = (params) => {
    if (!params) { return; }
    const clickedSprintIndex = params.dataIndex;
    const clickedissueIndex = params.componentIndex;
    const seriesName = params.seriesName;
    const clickedSprint = Array.isArray(yAxisDataArray) ? yAxisDataArray[clickedSprintIndex] : undefined;
    onChartClick && onChartClick(params, props);
    const environment = props.environment;
    if (onSprintClick) {
      onSprintClick(clickedSprint, seriesName, clickedissueIndex, environment);
    }
  };
  const legendSelectedRef = useRef(legendSelected);
  const handleLegendSelectChanged = (params) => {
    if (!params?.selected) { return; }
    const selectedValues = Object.values(params.selected);
    const allFalse = selectedValues.every((value) => value === false);
    console.log("params", params);
    console.log("selectedValues", selectedValues);
    console.log("allFalse", allFalse);
    if (allFalse) {
      console.log("happen if");

      const YFilledHide = {
        yAxis: {
          type: 'value',
          name: yField,
          nameLocation: "middle",
          show: false,
          minInterval: 1,
          ...getYAxisIndex(),
          axisLine: {
            show: YaxisLine ? YaxisLine : true,
          },
          axisTick: {
            show: false,
          },
          axisLabel: {
            show: true,
            formatter: function (value) {
              return formatYAxis(value);
            },
          },
          nameGap: yNameGap ? yNameGap : 30,
          ...otherYAxisProperty
        },
      };
      if (chartInstanceRef.current && !chartInstanceRef.current.isDisposed?.()) {
        chartInstanceRef.current.setOption(YFilledHide);
      }
    } else {
      console.log("happen else");

      const YFilledShow = {
        yAxis: {
          type: 'value',
          name: yField,
          nameLocation: "middle",
          show: true,
          minInterval: 1,
          ...getYAxisIndex(),
          axisLine: {
            show: YaxisLine ? YaxisLine : true,
          },
          axisTick: {
            show: false,
          },
          axisLabel: {
            show: true,
            formatter: function (value) {
              return formatYAxis(value);
            },
          },
          nameGap: yNameGap ? yNameGap : 30,
          ...otherYAxisProperty
        },
      };

      if (chartInstanceRef.current && !chartInstanceRef.current.isDisposed?.()) {
        chartInstanceRef.current.setOption(YFilledShow);
      }
    }
    // setLegendSelected(params.selected);
    if (params.selected !== legendSelectedRef.current) {
      legendSelectedRef.current = params.selected;
    }
  };

  const handleHighlight = (params) => {
    const chart = chartInstanceRef.current;
    if (!chart) { return; }
    const currentSelected = legendSelectedRef.current;
    if (params.seriesName && currentSelected[params.seriesName] === false) {
      chart.dispatchAction({
        type: 'downplay',
      });
    }
  };

  const onEvents = {
    'click': handleChartClick,
    'legendselectchanged': handleLegendSelectChanged,
    'highlight': handleHighlight,
  };

  useEffect(() => {
    if (props.events && props.events.selected) {
      setLegendSelected(props.events.selected);
    }
  }, [props.events]);

  return (
    <div style={chartheight ? { height: '18vw' } : {}}>
      <ReactECharts option={getStackOption()} style={targetHeight ? { height: '300px' } : { height: '281px' }} onEvents={events ? onEvents : undefined} onChartReady={handleChartReady} />
    </div>
  );
};

function areEqual(prevProps, nextProps) {
  if (_.isEqual(prevProps.data, nextProps.data) &&
    _.isEqual(prevProps.yAxisData, nextProps.yAxisData) &&
    _.isEqual(prevProps.yField, nextProps.yField) &&
    _.isEqual(prevProps.xField, nextProps.xField) &&
    _.isEqual(prevProps.yNameGap, nextProps.yNameGap) &&
    _.isEqual(prevProps.xNameGap, nextProps.xNameGap) &&
    _.isEqual(prevProps.barWidth, nextProps.barWidth) &&
    _.isEqual(prevProps.spaceTop, nextProps.spaceTop) &&
    _.isEqual(prevProps.spaceLeft, nextProps.spaceLeft) &&
    _.isEqual(prevProps.spaceRight, nextProps.spaceRight) &&
    _.isEqual(prevProps.spaceBottom, nextProps.spaceBottom) &&
    _.isEqual(prevProps.yAxisTextColor, nextProps.yAxisTextColor) &&
    _.isEqual(prevProps.yAxisDataArray, nextProps.yAxisDataArray) &&
    _.isEqual(prevProps.toolTipData, nextProps.toolTipData) &&
    _.isEqual(prevProps.isScrollEndLimit, nextProps.isScrollEndLimit) &&
    _.isEqual(prevProps.isScrollStartLimit, nextProps.isScrollStartLimit) &&
    _.isEqual(prevProps.borderRadius, nextProps.borderRadius) &&
    _.isEqual(prevProps.showLegend, nextProps.showLegend) &&
    _.isEqual(prevProps.sprintTotalCount, nextProps.sprintTotalCount) &&
    _.isEqual(prevProps.isScrollbarheight, nextProps.isScrollbarheight) &&
    _.isEqual(prevProps.color, nextProps.color) &&
    _.isEqual(prevProps.otherSeriesProperty, nextProps.otherSeriesProperty) &&
    _.isEqual(prevProps.otherYAxisProperty, nextProps.otherYAxisProperty) &&
    _.isEqual(prevProps.selectedMode, nextProps.selectedMode) &&
    _.isEqual(prevProps.otherXAxisProperty, nextProps.otherXAxisProperty)

  ) {
    return true;
  } else {
    return false;
  }
}
StackedVerticalBar.propTypes = {
  barWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  borderRadius: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.arrayOf(PropTypes.number)
  ]),
  chartHover: PropTypes.string,
  chartheight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      color: PropTypes.string,
      data: PropTypes.array.isRequired,
      otherSeriesProperty: PropTypes.object
    })
  ).isRequired,
  events: PropTypes.object,
  isScroll: PropTypes.bool,
  isScrollEndLimit: PropTypes.number,
  isScrollStartLimit: PropTypes.number,
  isScrollbarheight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChartClick: PropTypes.func,
  onSprintClick: PropTypes.func,
  otherSeriesProperty: PropTypes.object,
  otherXAxisProperty: PropTypes.object,
  otherYAxisProperty: PropTypes.object,
  selectedMode: PropTypes.string,
  showLegend: PropTypes.bool,
  spaceBottom: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  spaceLeft: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  spaceRight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  spaceTop: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  targetHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  toolTipConfig: PropTypes.object,
  xField: PropTypes.string,
  xNameGap: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  yAxisDataArray: PropTypes.array,
  yField: PropTypes.string,
  yNameGap: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  YaxisLine: PropTypes.bool,
  environment: PropTypes.any,
  is3DEfffect: PropTypes.bool,
  color: PropTypes.array,
  markPointData: PropTypes.array,
};

export default React.memo(StackedVerticalBar, areEqual);